import { useCallback, useEffect, useMemo } from 'react'
import { normalizeFavoritePath } from '../logic/favorites'
import {
  DEFAULT_FAVORITE_FILTER,
  DEFAULT_HIDDEN_FILTER,
  effectiveMenuFilters,
  menuShows,
  showsEmptyFolders,
  type FavoriteFilter,
  type HiddenFilter,
  type MenuFilters,
} from '../logic/menuFilters'
import { useGlobalSidebarStore } from '../stores/useGlobalSidebarStore'
import { useMenuFiltersStore } from '../stores/useMenuFiltersStore'
import { useFavoritePaths, useHiddenPaths } from './useHiddenPages'

/**
 * A sidebar menu's filters (see menuFilters): the eye's and the heart's
 * choices for the menu `menu` (its area), as they apply to the pages in it
 * (`paths`). `current` is the open page's row, shown whatever its mark.
 */
export function useMenuFilter(
  menu: string,
  paths: readonly string[],
  { searching = false, current = null }: { searching?: boolean; current?: string | null } = {}
) {
  const hidden = useHiddenPaths()
  const favorites = useFavoritePaths()
  const chosenHidden = useMenuFiltersStore((s) => s.hiddenFilters[menu] ?? DEFAULT_HIDDEN_FILTER)
  const chosenFavorite = useMenuFiltersStore(
    (s) => s.favoriteFilters[menu] ?? DEFAULT_FAVORITE_FILTER
  )
  const heldPaths = useMenuFiltersStore((s) => s.heldPaths)
  const setHidden = useMenuFiltersStore((s) => s.setHiddenFilter)
  const setFavorite = useMenuFiltersStore((s) => s.setFavoriteFilter)

  const normalizedPaths = useMemo(() => paths.map(normalizeFavoritePath), [paths])
  const effective = useMemo(
    () =>
      effectiveMenuFilters(normalizedPaths, hidden, favorites, {
        hiddenFilter: chosenHidden,
        favoriteFilter: chosenFavorite,
      }),
    [chosenFavorite, chosenHidden, favorites, hidden, normalizedPaths]
  )
  const currentPath = current ? normalizeFavoritePath(current) : null
  const filters: MenuFilters = useMemo(
    () => ({
      hidden,
      favorites,
      hiddenFilter: effective.hiddenFilter,
      favoriteFilter: effective.favoriteFilter,
      shownAnyway: new Set(currentPath ? [currentPath, ...heldPaths] : heldPaths),
      searching,
    }),
    [currentPath, effective, favorites, heldPaths, hidden, searching]
  )
  const shows = useCallback(
    (path: string) => menuShows(normalizeFavoritePath(path), filters),
    [filters]
  )

  return {
    hasHidden: effective.hasHidden,
    hasFavorites: effective.hasFavorites,
    hiddenFilter: effective.hiddenFilter,
    favoriteFilter: effective.favoriteFilter,
    /** The menu shows the row for the page at this address. */
    shows,
    showsEmptyFolders: showsEmptyFolders(filters),
    setHiddenFilter: useCallback(
      (filter: HiddenFilter) => setHidden(menu, filter),
      [menu, setHidden]
    ),
    setFavoriteFilter: useCallback(
      (filter: FavoriteFilter) => setFavorite(menu, filter),
      [menu, setFavorite]
    ),
  }
}

/** The attribute naming the page a hideable menu row is for. */
export const MENU_ROW_PATH_ATTRIBUTE = 'data-menu-row-path'

/** Props marking a hideable menu row with its page, for the held-row release. */
export function menuRowPathProps(path: string) {
  return { [MENU_ROW_PATH_ATTRIBUTE]: normalizeFavoritePath(path) }
}

/**
 * A row hidden while its menu was in use shows until the pointer leaves that
 * row, then goes; if the menu stops being used first (the pointer leaves it,
 * focus moves on), it goes then. A row's own options menu counts as the row.
 * Used once, where the menus render.
 */
export function useReleaseHeldRowsWhenIdle() {
  useEffect(
    () =>
      useGlobalSidebarStore.subscribe((state, previous) => {
        if (previous.drawerInUse && !state.drawerInUse) {
          useMenuFiltersStore.getState().releaseHeld()
        }
      }),
    []
  )

  // Listens for the pointer only while a row is held.
  useEffect(() => {
    const releaseOffRow = (event: PointerEvent) => {
      if (!(event.target instanceof Element)) return
      if (event.target.closest('[role="menu"]')) return
      const row = event.target.closest(`[${MENU_ROW_PATH_ATTRIBUTE}]`)
      useMenuFiltersStore
        .getState()
        .releaseHeldExcept(row?.getAttribute(MENU_ROW_PATH_ATTRIBUTE) ?? null)
    }
    let listening = false
    const sync = (held: readonly string[]) => {
      if (held.length > 0 && !listening) {
        document.addEventListener('pointermove', releaseOffRow)
        document.addEventListener('pointerover', releaseOffRow)
        listening = true
      } else if (held.length === 0 && listening) {
        document.removeEventListener('pointermove', releaseOffRow)
        document.removeEventListener('pointerover', releaseOffRow)
        listening = false
      }
    }
    sync(useMenuFiltersStore.getState().heldPaths)
    const unsubscribe = useMenuFiltersStore.subscribe((state) => sync(state.heldPaths))
    return () => {
      unsubscribe()
      sync([])
    }
  }, [])
}
